import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone } from '@angular/core';
import { LoadScriptOptions, ScriptPosition } from '@core/services/load-scripts/load-scripts';
import { PlatformService } from '@core/services/platform.service';
import { ServerTokensService } from '@core/services/server-tokens.service';

@Injectable({
    providedIn: 'root'
})
export class LoadScriptsService {
    private prefix = 'load-script-';
    private defaultOptions: LoadScriptOptions = {
        async: true,
        position: 'beforeHeadEnd'
    };

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private readonly ngZone: NgZone,
        private readonly platformService: PlatformService,
        private readonly serverTokensService: ServerTokensService
    ) {}

    public loadScript(url: string, scriptOptions?: LoadScriptOptions): Promise<boolean> {
        return new Promise((resolve, reject) => {
            if (this.platformService.isBrowser || !this.serverTokensService.isBot) {
                if (this.checkScriptInDOM(url)) {
                    return resolve(true);
                }

                const options: LoadScriptOptions = {
                    ...this.defaultOptions,
                    ...scriptOptions
                };

                const script = this.createScript(options);
                script.src = url;
                script.onload = () => {
                    this.ngZone.runOutsideAngular(() => {
                        setTimeout(() => resolve(true), 1);
                    });
                };
                script.onerror = () => {
                    reject(new Error(`Script load: Failed to load script: ${url}`));
                };
                this.insertScript(script, options.position);
            } else {
                return resolve(false);
            }
        });
    }

    public loadSnippet(snippet: string, id: string, scriptOptions?: LoadScriptOptions): Promise<boolean> {
        if (this.serverTokensService.isBot || this.platformService.isServer) {
            return Promise.resolve(false);
        }

        if (this.document.getElementById(`${this.prefix}${id}`) || this.platformService.isServer) {
            return Promise.resolve(false);
        }

        return new Promise(resolve => {
            const options: LoadScriptOptions = {
                ...this.defaultOptions,
                ...scriptOptions
            };

            const scriptSnippet = this.createScript(options);
            scriptSnippet.innerHTML = snippet;
            scriptSnippet.id = `${this.prefix}${id}`;

            this.insertScript(scriptSnippet, options.position);
            return resolve(true);
        });
    }

    public removeScript(src: string): void {
        if (this.platformService.isServer) {
            return;
        }

        const script = this.checkScriptInDOM(src);

        if (script) {
            script.parentElement?.removeChild(script);
        }
    }

    private checkScriptInDOM = (src: string): HTMLScriptElement | undefined => {
        const scripts = this.document.getElementsByTagName('script');
        for (let i = scripts.length; i--; ) {
            if (scripts[i].src === src) {
                return scripts[i];
            }
        }
        return undefined;
    };

    private createScript(options?: LoadScriptOptions): HTMLScriptElement {
        const scriptElement = this.document.createElement('script') as HTMLScriptElement;
        scriptElement.async = options && typeof options.async !== 'undefined' ? options.async : true;
        if (options?.attributes) {
            Object.entries(options.attributes).forEach(([key, value]) => {
                scriptElement.setAttribute(key, value);
            });
        }

        return scriptElement;
    }

    private insertScript(script: HTMLScriptElement, position?: ScriptPosition): void {
        if (position === 'beforeBodyEnd') {
            this.document.body.appendChild(script);
            return;
        }

        this.document.getElementsByTagName('head')[0].appendChild(script);
    }
}
